import { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

export type UsePaginationOptions = {
  /** 페이지당 항목 수 (기본 10) */
  pageSize?: number;
  /** 페이지 크기 선택 옵션 */
  pageSizeOptions?: readonly number[];
  /** 검색·필터 변경 시 1페이지로 리셋할 의존성 */
  resetDeps?: readonly unknown[];
};

export function usePagination<T>(
  items: T[],
  options?: UsePaginationOptions
) {
  const pageSizeOptions = options?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const [pageSize, setPageSizeState] = useState(options?.pageSize ?? DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);

  const resetKey = options?.resetDeps
    ? JSON.stringify(options.resetDeps)
    : "";

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [safePage, page]);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalItems);

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  return {
    paginatedItems,
    page: safePage,
    pageSize,
    totalItems,
    totalPages,
    rangeStart,
    rangeEnd,
    setPage,
    setPageSize,
    pageSizeOptions,
  };
}
